import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

import { Checkbox, StyledTextField } from '@labradorsports/components';
import { useSite } from '@labradorsports/utils';

import { useDispatcher } from '../../shared/hooks/index.js';
import { addValidation, useNavigateWithQuery, useValidation } from '../../shared/providers/index.js';
import { HelmetWrapper } from '../../shared/structural/index.js';
import { pushDataLayerEvent } from '../../shared/utils.js';
import { Selectors, useCaptchaCreateAccountMutation, authActions } from '../../store/index.js';

const SignupForm: FC = () => {
    const { Config } = useSite();
    const [createAccount] = useCaptchaCreateAccountMutation();
    const setLoginEmail = useDispatcher(authActions.SetLoginEmail);
    const navigate = useNavigateWithQuery();
    const accountFormError = useSelector(Selectors.accountFormError);
    const email = useSelector(Selectors.loginEmail);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [tosAccepted, setTosAccepted] = useState(false);

    const { validateRef, errors, submitted, setSubmitted } = useValidation(() => {
        const newError: any = {};

        if (firstName === '') {
            newError.firstName = true;
        }

        if (lastName === '') {
            newError.lastName = true;
        }

        if (email === '') {
            newError.email = true;
        }

        if (password === '') {
            newError.password = true;
        }

        if (password !== confirmPassword) {
            newError.passwordMatch = true;
        }

        if (!tosAccepted) {
            newError.tos = true;
        }

        return newError;
    }, [firstName, lastName, email, password, confirmPassword, tosAccepted]);

    const signup = async () => {
        setSubmitted(false);

        if (await validateRef.current()) {
            const { data } = await createAccount({ email, password, firstName, lastName });

            if (data) {
                pushDataLayerEvent('sign_up', {
                    site: Config.SiteConfig.Site,
                    uid: data.uid,
                });

                navigate('/feed');
            }
        } else {
            setSubmitted(true);
        }
    };

    const emailChange = (event: any) => {
        setLoginEmail(event.target.value);
    };

    const passwordChange = (event: any) => {
        setPassword(event.target.value);
    };

    const confirmPasswordChange = (event: any) => {
        setConfirmPassword(event.target.value);
    };

    const firstNameChange = (event: any) => {
        setFirstName(event.target.value);
    };

    const lastNameChange = (event: any) => {
        setLastName(event.target.value);
    };

    const keyPress = (event: any) => {
        if (event.key === 'Enter') {
            signup();
        }
    };

    const toggleTOS = (event: any, checked: boolean) => {
        setTosAccepted(checked);
    };

    const canonical = `${Config.SiteConfig.Url}/signup`;

    return (
        <div className="signup-form">
            <HelmetWrapper title="Sign Up">
                <link rel="canonical" href={canonical} />
                <meta property="og:url" content={canonical} />
            </HelmetWrapper>
            <div>
                <StyledTextField
                    value={firstName}
                    onChange={firstNameChange}
                    label="First Name"
                    className="first-name-field"
                    onKeyPress={keyPress}
                    helperText={submitted && errors.firstName && 'First name is required'}
                    error={submitted && errors.firstName}
                />
                <StyledTextField
                    value={lastName}
                    onChange={lastNameChange}
                    label="Last Name"
                    className="last-name-field"
                    onKeyPress={keyPress}
                    helperText={submitted && errors.lastName && 'Last name is required'}
                    error={submitted && errors.lastName}
                />
            </div>
            <StyledTextField
                value={email}
                onChange={emailChange}
                label="Email"
                onKeyPress={keyPress}
                helperText={submitted && errors.email && 'Email is required'}
                error={submitted && errors.email}
                fullWidth
            />
            <StyledTextField
                type="password"
                value={password}
                onChange={passwordChange}
                label="Password"
                onKeyPress={keyPress}
                helperText={submitted && errors.password && 'Password is required'}
                error={submitted && errors.password}
                fullWidth
            />
            <StyledTextField
                type="password"
                value={confirmPassword}
                onChange={confirmPasswordChange}
                label="Confirm Password"
                onKeyPress={keyPress}
                helperText={submitted && errors.passwordMatch && 'Passwords do not match'}
                error={submitted && errors.passwordMatch}
                fullWidth
            />
            <div className="d-table">
                <Checkbox
                    checked={tosAccepted}
                    onChange={toggleTOS}
                    data-testid="tos-checkbox"
                    labelComponent={
                        <span className="small d-table-cell align-middle">
                            I have read and accept the{' '}
                            <a
                                href="https://app.termly.io/document/terms-of-use-for-saas/3e7f14b7-2cec-45f4-a500-d8c920b9036f"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms of Use
                            </a>
                        </span>
                    }
                />
            </div>
            {submitted && errors.tos ? (
                <span className="error-text mb-2">You must accept the Terms of Service</span>
            ) : undefined}
            {accountFormError ? <span className="error-text mb-2">{accountFormError.message}</span> : undefined}

            <Button variant="contained" onClick={signup} color="secondary" data-testid="signup-submit-btn" fullWidth>
                Sign Up Free
            </Button>
        </div>
    );
};

export default addValidation(SignupForm);
